// MultiSourceResultsTable — shared renderer for the 7-column results table
// used by both PrintReport.js and ViewTest.js. Reads each disk's
// `interpretations` array (one entry per source: EUCAST, CLSI, optionally
// Custom) and renders one column per source. The org's primary guideline
// gets a subtle grey background to indicate it as the lab's policy choice.
//
// Cell layout per (disk, source) — two lines, both centered:
//   Top:    SIR letter (colored, bold) optionally followed by a footnote
//           symbol (same size, grey) if a special provenance applies.
//   Bottom: Breakpoint pair "R≤r, S≥s" same size as the SIR letter, grey.
// Empty/no-interpretation cells show "-" (single dash, centered).
//
// SIR letter coloring matches the existing app convention:
//   S = success.main (green)
//   I = warning.main (amber)
//   R = error.main (red)
//
// Footnote symbols (medical-publishing convention):
//   *  Custom breakpoint (org-defined rule matched)
//   †  Intrinsic resistance (organism intrinsically resistant)
//   ‡  Human breakpoint used as fallback (animal sample, no animal bp)
//   –  No breakpoint defined (rendered as the entire cell content; no symbol)
//
// The footer beneath the table conditionally lists ONLY the symbols that
// actually appear in the rendered table — so a table without any custom
// rules won't include the "*" entry in its footer. The set of used
// footnotes is computed in a pre-render pass over the data (NOT by
// mutation during cell rendering — that produced a known bug where the
// footer Set was empty at the time the parent's JSX was evaluated).

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

const SIR_COLORS = {
  S: "success.main",
  I: "warning.main",
  R: "error.main",
  SDD: "warning.main",
};

// Background tint for the primary-guideline column. Subtle — readable on
// screen and prints cleanly without dominating the table.
const PRIMARY_COL_BG = "rgba(0, 0, 0, 0.04)";

// Footnote symbol mapping. Order in the footer follows medical-publishing
// convention: *, †, ‡, §. We currently use the first three.
const FOOTNOTE_SYMBOLS = {
  custom: "*",
  intrinsic: "†",
  fallback: "‡",
};

const FOOTNOTE_LABELS = {
  custom: "printReport.footnoteCustom",
  intrinsic: "printReport.footnoteIntrinsic",
  fallback: "printReport.footnoteFallback",
  noBp: "printReport.footnoteNoBp",
};

/**
 * Find the interpretation entry for a given source label. Returns null if
 * no entry exists (e.g. CUSTOM column for a disk where no custom rule matched).
 */
function findEntry(interpretations, source) {
  if (!Array.isArray(interpretations)) return null;
  return interpretations.find((i) => i?.source === source) || null;
}

/**
 * Format a breakpoint pair as "R≤r, S≥s" (or partial if only one is defined).
 * Returns null if no breakpoints are present.
 */
function formatBreakpointPair(entry) {
  if (!entry) return null;
  const s = entry.breakpointS;
  const r = entry.breakpointR;
  if (s != null && r != null) return `R≤${r}, S≥${s}`;
  if (s != null) return `S≥${s}`;
  if (r != null) return `R≤${r}`;
  return null;
}

/**
 * Determine which footnote (if any) applies to an interpretation entry.
 * Returns one of "custom" / "intrinsic" / "fallback" / "noBp" / null.
 */
function footnoteKeyFor(entry) {
  if (!entry) return null;
  if (entry.sourceKind === "custom") return "custom";
  if (entry.sourceKind === "intrinsic") return "intrinsic";
  if (
    entry.sourceKind === "amrie_human_fallback" ||
    entry.isFallback === true
  ) {
    return "fallback";
  }
  // Entry exists but has no interpretation → "no breakpoint" footnote
  if (!entry.interpretation) return "noBp";
  return null;
}

/**
 * Pre-render pass: walk every (disk × source) and collect the set of
 * footnote keys that will actually appear in the rendered table. Returns
 * a plain array of keys in fixed display order.
 *
 * Done before render so the conditional footer below the table can be
 * decided synchronously inside the parent component.
 */
function computeUsedFootnotes(diskAssessments) {
  const used = new Set();
  for (const da of diskAssessments || []) {
    for (const source of ["EUCAST", "CLSI", "Custom"]) {
      const entry = findEntry(da?.interpretations, source);
      if (!entry) continue; // No entry at all → no footnote
      const key = footnoteKeyFor(entry);
      if (key) used.add(key);
    }
  }
  // Return in display order (matches FOOTNOTE_SYMBOLS object key order
  // for *, †, ‡, then noBp last).
  return ["custom", "intrinsic", "fallback", "noBp"].filter((k) => used.has(k));
}

/**
 * Render one cell's content for a (disk, source) pair: two centered lines.
 *   Line 1: SIR letter (colored, bold) with optional footnote symbol.
 *   Line 2: breakpoint pair, same font size as the SIR letter, grey.
 * Falls back to a single centered "-" if no entry or no interpretation.
 */
function SourceCell({ entry }) {
  // No entry at all (e.g. CUSTOM column for a disk with no custom rule).
  if (!entry) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ fontSize: "0.875rem" }} color="text.secondary">
          -
        </Typography>
      </Box>
    );
  }

  const sir = entry.interpretation;

  // Entry exists but no interpretation. Show "-" — the "noBp" footnote
  // is registered separately by the pre-render pass.
  if (!sir) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <Typography sx={{ fontSize: "0.875rem" }} color="text.secondary">
          -
        </Typography>
      </Box>
    );
  }

  const bp = formatBreakpointPair(entry);
  const footnoteKey = footnoteKeyFor(entry);
  // Don't render "noBp" symbol on cells with a real interpretation.
  const footnoteSym =
    footnoteKey && footnoteKey !== "noBp"
      ? FOOTNOTE_SYMBOLS[footnoteKey]
      : null;

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: SIR_COLORS[sir] || "text.primary",
          fontWeight: "bold",
          lineHeight: 1.25,
        }}
      >
        {sir}
        {footnoteSym && (
          <Typography
            component="span"
            sx={{
              fontSize: "1em",
              color: "text.secondary",
              fontWeight: "bold",
              ml: 0.5,
            }}
          >
            {footnoteSym}
          </Typography>
        )}
      </Typography>
      {bp && (
        <Typography
          color="text.secondary"
          sx={{ display: "block", lineHeight: 1.25, fontSize: "0.875rem" }}
        >
          {bp}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Build a column-header label for a guideline. Includes the year if any
 * entry across the disks has one, e.g. "EUCAST 2026". For Custom, just
 * "CUSTOM" (no year).
 */
function buildSourceHeader(diskAssessments, source) {
  if (source === "Custom") return "CUSTOM";
  // Find the first disk with an entry for this source and use its year.
  for (const da of diskAssessments || []) {
    const entry = findEntry(da?.interpretations, source);
    if (entry?.year) return `${source} ${entry.year}`;
  }
  return source;
}

/**
 * Main exported component: the 7-column results table plus its conditional
 * footnotes footer.
 *
 * Props:
 *   diskAssessments  — the array straight off the test document
 *   primaryGuideline — "EUCAST" or "CLSI"; controls which column gets
 *                      the highlight background. Default "EUCAST".
 */
export default function MultiSourceResultsTable({
  diskAssessments,
  primaryGuideline = "EUCAST",
}) {
  const { t } = useTranslation();
  if (!Array.isArray(diskAssessments) || diskAssessments.length === 0) {
    return (
      <Typography sx={{ fontSize: "0.875rem", mt: 2 }} color="text.secondary">
        {t("printReport.noDiskAssessments")}
      </Typography>
    );
  }

  const eucastHeader = buildSourceHeader(diskAssessments, "EUCAST");
  const clsiHeader = buildSourceHeader(diskAssessments, "CLSI");
  const customHeader = buildSourceHeader(diskAssessments, "Custom");

  const eucastBg = primaryGuideline === "EUCAST" ? PRIMARY_COL_BG : undefined;
  const clsiBg = primaryGuideline === "CLSI" ? PRIMARY_COL_BG : undefined;

  // Cell-level styles — matches the audit-table cells (MUI default 0.875rem)
  // for a uniform body-text scale across the whole report.
  const headerSx = {
    fontWeight: "bold",
    whiteSpace: "nowrap",
    fontSize: "0.875rem",
    textAlign: "center",
  };
  const cellSx = {
    fontSize: "0.875rem",
    verticalAlign: "middle",
  };

  // Pre-render pass: determine which footnote keys actually apply across
  // the data. Done before render rather than during so the conditional
  // footer can resolve synchronously.
  const usedFootnotes = computeUsedFootnotes(diskAssessments);

  return (
    <Box>
      <TableContainer sx={{ mt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ ...headerSx, textAlign: "left" }}>#</TableCell>
              <TableCell sx={{ ...headerSx, textAlign: "left" }}>
                {t("printReport.tableHeaderDrug")}
              </TableCell>
              <TableCell sx={{ ...headerSx, textAlign: "left" }}>
                {t("printReport.tableHeaderPotency")}
              </TableCell>
              <TableCell sx={{ ...headerSx, textAlign: "left" }}>
                {t("printReport.tableHeaderZone")}
              </TableCell>
              <TableCell sx={{ ...headerSx, backgroundColor: eucastBg }}>
                {eucastHeader}
              </TableCell>
              <TableCell sx={{ ...headerSx, backgroundColor: clsiBg }}>
                {clsiHeader}
              </TableCell>
              <TableCell sx={headerSx}>{customHeader}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diskAssessments.map((da, i) => {
              const eucastEntry = findEntry(da?.interpretations, "EUCAST");
              const clsiEntry = findEntry(da?.interpretations, "CLSI");
              const customEntry = findEntry(da?.interpretations, "Custom");
              const zoneText =
                da?.mode === "NZ"
                  ? "NZ"
                  : da?.diameterMm != null
                    ? `${Math.round(da.diameterMm)} mm`
                    : "-";
              return (
                <TableRow
                  key={da?.antibioticId ? `${da.antibioticId}-${i}` : i}
                >
                  <TableCell sx={cellSx}>{i + 1}</TableCell>
                  <TableCell sx={cellSx}>
                    {da?.antibioticName || da?.ab || "-"}
                    {da?.antibioticSymbol &&
                      da.antibioticSymbol !== da.antibioticName && (
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", display: "block" }}
                        >
                          {da.antibioticSymbol}
                        </Typography>
                      )}
                  </TableCell>
                  <TableCell sx={cellSx}>{da?.potency || "-"}</TableCell>
                  <TableCell sx={cellSx}>{zoneText}</TableCell>
                  <TableCell sx={{ ...cellSx, backgroundColor: eucastBg }}>
                    <SourceCell entry={eucastEntry} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, backgroundColor: clsiBg }}>
                    <SourceCell entry={clsiEntry} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <SourceCell entry={customEntry} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Conditional footnotes footer — only shows symbols actually used */}
      {usedFootnotes.length > 0 && (
        <Box sx={{ mt: 1.5, pl: 1 }}>
          {usedFootnotes.map((k) => {
            const sym = FOOTNOTE_SYMBOLS[k] || "–";
            const labelKey = FOOTNOTE_LABELS[k];
            return (
              <Typography
                key={k}
                color="text.secondary"
                sx={{ fontSize: "0.875rem", lineHeight: 1.5 }}
              >
                <Typography component="span" sx={{ fontWeight: "bold", mr: 1 }}>
                  {sym}
                </Typography>
                {labelKey ? t(labelKey) : ""}
              </Typography>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
